import { type KeyboardEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Icon, IconButton } from '@socialchorus/shared-ui-components';
import Spinner from '../../../../components/ui/spinner';
import type { ContentFile } from '../../../../models/user-generated-contents/types';
import { MAX_FILES } from './use-file-uploader';
import { getPreviewUrl } from './file-types';
import {
  useSortableItem,
  SortableGrid,
  type DragHandleProps,
} from './sortable';
import './files.scss';

type FilePlaceholderProps = {
  fileName: string | undefined;
  fileType: string | undefined;
  previewUrl?: string;
};

const FilePlaceholder = ({
  fileName,
  fileType,
  previewUrl,
}: FilePlaceholderProps) => {
  const iconUrl = previewUrl ?? getPreviewUrl(fileType);
  return (
    <div className="ugc-files__placeholder">
      {iconUrl && (
        <img
          src={iconUrl}
          alt=""
          draggable={false}
          className="ugc-files__placeholder-icon"
          data-testid="ugc-file-icon"
        />
      )}
      <span className="ugc-files__placeholder-name">{fileName}</span>
    </div>
  );
};

type SortableFileItemProps = {
  file: ContentFile;
  index: number;
  onDelete: (index: number) => void;
};

const SortableFileItem = ({ file, index, onDelete }: SortableFileItemProps) => {
  const { t } = useTranslation();
  const { setNodeRef, style, attributes, listeners } = useSortableItem(
    file.url ?? `file-${index}`
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="ugc-files__item"
      {...attributes}
      {...listeners}
    >
      <FilePlaceholder
        fileName={file.file_name}
        fileType={file.file_type}
        previewUrl={file.preview_url}
      />
      <div className="ugc-files__item-overlay">
        <IconButton
          iconName="delete"
          size="compact"
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onDelete(index);
          }}
          onKeyDown={(e: KeyboardEvent) => e.stopPropagation()}
          aria-label={t('user_generated_content.media.files.delete')}
          className="bg-black-05"
        />
      </div>
    </div>
  );
};

type FilesProps = {
  files: ContentFile[];
  inProgress: File[];
  totalCount: number;
  // When true, the counter renders in its error state (limit exceeded).
  limitError?: boolean;
  onDelete: (index: number) => void;
  onReorder: (files: ContentFile[]) => void;
  dragHandleProps?: DragHandleProps;
  // True while this group is being dragged / keyboard-selected
  isDragging?: boolean;
};

const Files = ({
  files,
  inProgress,
  totalCount,
  limitError,
  onDelete,
  onReorder,
  dragHandleProps,
  isDragging,
}: FilesProps) => {
  const { t } = useTranslation();

  return (
    <div className="ugc-files__section">
      <div
        className={`ugc-files__section-header${
          dragHandleProps ? ' ugc-files__section-header--draggable' : ''
        }${isDragging ? ' ugc-files__section-header--dragging' : ''}`}
        {...dragHandleProps}
      >
        {dragHandleProps && (
          <Icon
            className={`ugc-files__drag-handle${
              isDragging ? ' ugc-files__drag-handle--active' : ''
            }`}
            size={20}
            aria-hidden
          >
            drag_indicator
          </Icon>
        )}
        <Badge
          color={limitError ? 'negative' : 'default'}
          text={t('user_generated_content.media.files.section_label', {
            count: totalCount,
            max: MAX_FILES,
          })}
        />
      </div>
      <SortableGrid
        items={files}
        getId={(f) => f.url ?? `file-${files.indexOf(f)}`}
        onReorder={(reordered) =>
          onReorder(
            reordered.map((f: ContentFile, i: number) => ({ ...f, order: i }))
          )
        }
      >
        <div className="ugc-files__grid">
          {files.map((file, i) => (
            <SortableFileItem
              key={file.url ?? i}
              file={file}
              index={i}
              onDelete={onDelete}
            />
          ))}
          {inProgress.map((file) => (
            <div
              key={`${file.name}-${file.lastModified}`}
              className="ugc-files__item ugc-files__item--uploading"
            >
              <FilePlaceholder fileName={file.name} fileType={file.type} />
              <div className="ugc-files__uploading-overlay">
                <Spinner loading />
                <span className="sr-only" role="status">
                  {t('common.uploading')}
                </span>
              </div>
            </div>
          ))}
        </div>
      </SortableGrid>
    </div>
  );
};

export default Files;
